import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter, useRouteError } from "react-router-dom";
import {
  withChunkReload,
  isChunkLoadError,
  canAutoReload,
  markAndReload,
  revealErrorUI,
  chunkErrorColors,
} from "@/utils/chunkReload";
import {
  ChunkLoadBoundary,
  ChunkErrorCard,
} from "@/components/ChunkLoadBoundary/ChunkLoadBoundary";

// MainShell 走 lazy：main entry 不静态依赖 antd / antd-mobile / utils 全家
const mainShellPromise = import("./MainShell");
// withChunkReload：MainShell 是首屏唯一入口，网络抖动拉不到它时如果不兜底，
// 错误会直接冒泡到 React Router 默认错误页（"Unexpected Application Error!"）。
// 这里先自动刷新一次；刷新预算耗尽再由 ChunkLoadBoundary 给出重试按钮。
const MainShell = lazy(withChunkReload(() => mainShellPromise));

// 提前发起 HomePage import，与 MainShell 并行下载（同 promise 缓存被复用，不会重复请求）
import("../pages/HomePage").catch(() => {});

/**
 * 路由级 errorElement：最后一道防线。
 *
 * chunk 错误正常会被下面的 ChunkLoadBoundary 拦掉，但任何漏到 React Router
 * 顶层的（如页面内部自己发起的 dynamic import）此前会命中 RR 默认错误页
 * "Unexpected Application Error! / Failed to fetch dynamically imported module"。
 * 这里统一换成重试卡片；非 chunk 错误保持可诊断，不吞。
 */
function RouteErrorElement() {
  const error = useRouteError();
  const isChunk = isChunkLoadError(error);
  // 能走到这里说明这次 chunk 失败没被任何局部策略接住（页面内部自己发起的
  // dynamic import 等）。还有刷新预算就先自动刷一次，渲染 null 不闪卡片；
  // 预算耗尽才展示重试按钮。副作用放 effect 里，不在渲染期改动全局状态。
  const willReload = isChunk && canAutoReload();
  useEffect(() => {
    if (willReload) markAndReload();
  }, [willReload]);
  // 首屏阶段错误页同样会被 #app-loading 骨架埋住，先揭开再说
  useEffect(() => {
    if (!willReload) revealErrorUI();
  }, [willReload]);

  if (willReload) return null;
  if (isChunk) return <ChunkErrorCard />;

  // 非 chunk 错误：保持可诊断，绝不当成 chunk 错误吞掉或刷新。
  console.error("[router] unhandled route error:", error);
  const message =
    error instanceof Error ? error.message : String(error ?? "unknown error");
  // 配色同 ChunkErrorCard 取主题变量，否则浅色主题（pearl/blossom/meadow/apricot）
  // 会突然出现一个全屏深色块。
  const { fg, bg } = chunkErrorColors();
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: 24,
        background: bg,
        color: fg,
        fontSize: 14,
        boxSizing: "border-box",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        Unexpected Application Error!
      </div>
      <div style={{ wordBreak: "break-word" }}>{message}</div>
    </div>
  );
}

/** RR 6.30+ 运行时支持 v7_startTransition；部分版本的 FutureConfig 类型未收录该字段 */
type CreateBrowserRouterFuture = NonNullable<
  NonNullable<Parameters<typeof createBrowserRouter>[1]>["future"]
> & { v7_startTransition?: boolean };

export const router = createBrowserRouter(
  [
    {
      path: "/*",
      errorElement: <RouteErrorElement />,
      element: (
        <ChunkLoadBoundary>
          <Suspense fallback={null}>
            <MainShell />
          </Suspense>
        </ChunkLoadBoundary>
      ),
    },
  ],
  {
    future: { v7_startTransition: true } as CreateBrowserRouterFuture,
  }
);
